import { withKhata, withProjectKhata } from "../utils/finance";
import { formatCount, formatMoney, formatTimes } from "../utils/format";

const Stat = ({ label, amount, hint, tone }) => (
  <article className={`stat ${tone || ""}`}>
    <span>{label}</span>
    <strong>{formatMoney(amount)}</strong>
    {hint ? <small>{hint}</small> : null}
  </article>
);

const Meta = ({ label, amount, hint }) => (
  <span>
    {label}
    <b>{formatMoney(amount)}</b>
    {hint ? <small>{hint}</small> : null}
  </span>
);

const countHint = (count, text) => (Number(count) > 0 ? text : null);

export function KhataSummary({ khata, variant = "stats" }) {
  const data = withKhata(khata);

  if (variant === "card" || variant === "form") {
    return (
      <div className="sale-meta khata-meta">
        {variant === "card" ? (
          <Meta label="Bill" amount={data.totalBill} hint={countHint(data.expenseCount, formatCount(data.expenseCount, "expense"))} />
        ) : null}
        <Meta label="Udhar taken" amount={data.credit} hint={countHint(data.creditCount, formatTimes(data.creditCount))} />
        <Meta label="Still due" amount={data.payable} />
        <Meta label="Cash" amount={data.cash} hint={countHint(data.cashCount, formatTimes(data.cashCount))} />
        <Meta label="Cheque" amount={data.cheque} hint={countHint(data.chequeCount, formatTimes(data.chequeCount))} />
        <Meta label="Online" amount={data.online} hint={countHint(data.onlineCount, formatTimes(data.onlineCount))} />
        <Meta label="Pay order" amount={data.payOrder} hint={countHint(data.payOrderCount, formatTimes(data.payOrderCount))} />
        <Meta label="Bank" amount={data.bank} hint={countHint(data.bankCount, formatTimes(data.bankCount))} />
        <Meta label="Udhar paid back" amount={data.settled} hint={countHint(data.settledCount, formatTimes(data.settledCount))} />
        <Meta label="Paid" amount={data.totalPaid} hint={countHint(data.paymentCount, formatCount(data.paymentCount, "payment"))} />
      </div>
    );
  }

  return (
    <div className="stat-grid compact khata-stats">
      <Stat label="Bill" amount={data.totalBill} hint={countHint(data.expenseCount, formatCount(data.expenseCount, "expense"))} />
      <Stat label="Paid" amount={data.totalPaid} hint={countHint(data.paymentCount, formatCount(data.paymentCount, "payment"))} tone="stat-in" />
      <Stat label="Still due" amount={data.payable} tone="stat-out" />
      <Stat label="Cash" amount={data.cash} hint={countHint(data.cashCount, formatTimes(data.cashCount))} />
      <Stat label="Cheque" amount={data.cheque} hint={countHint(data.chequeCount, formatTimes(data.chequeCount))} />
      <Stat label="Online" amount={data.online} hint={countHint(data.onlineCount, formatTimes(data.onlineCount))} />
      <Stat label="Pay order" amount={data.payOrder} hint={countHint(data.payOrderCount, formatTimes(data.payOrderCount))} />
      <Stat label="Bank" amount={data.bank} hint={countHint(data.bankCount, formatTimes(data.bankCount))} />
      <Stat label="Udhar taken" amount={data.credit} hint={countHint(data.creditCount, formatTimes(data.creditCount))} tone="stat-now" />
      <Stat label="Udhar paid back" amount={data.settled} hint={countHint(data.settledCount, formatTimes(data.settledCount))} tone="stat-in" />
    </div>
  );
}

export function KhataRunning({ running, variant = "vendor" }) {
  if (!running) return null;
  if (variant === "project") {
    return (
      <span className="khata-running">
        Bill {formatMoney(running.totalBill)} · In {formatMoney(running.cashIn)} · Out{" "}
        {formatMoney(running.cashOut)} · Net {formatMoney(running.cashNet)} · Due{" "}
        {formatMoney(running.payable)}
      </span>
    );
  }
  return (
    <span className="khata-running">
      Bill {formatMoney(running.totalBill)} · Paid {formatMoney(running.totalPaid)} · Due{" "}
      {formatMoney(running.payable)}
    </span>
  );
}

export function ProjectKhataSummary({ khata, variant = "stats" }) {
  const data = withProjectKhata(khata);
  const salesHint = [
    countHint(data.saleCount, formatCount(data.saleCount, "sale")),
    data.activeSales > 0 ? `${data.activeSales} paying` : null,
    data.completedSales > 0 ? `${data.completedSales} paid off` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const expenseHint = countHint(data.expenseCount, formatCount(data.expenseCount, "expense"));
  const receivedHint = data.downPayment > 0 ? `Down ${formatMoney(data.downPayment)}` : null;

  if (variant === "card") {
    return (
      <div className="sale-meta khata-meta">
        <Meta label="Sales" amount={data.totalSale} hint={salesHint || null} />
        <Meta label="Received" amount={data.received} hint={receivedHint} />
        <Meta label="Remaining" amount={data.remaining} />
        <Meta label="Bill" amount={data.totalBill} hint={expenseHint} />
        <Meta label="Paid out" amount={data.totalPaid} />
        <Meta label="Udhar" amount={data.payable} />
        <Meta label="Cash in" amount={data.cashIn} />
        <Meta label="Cash out" amount={data.cashOut} />
        <Meta label="Net" amount={data.cashNet} />
      </div>
    );
  }

  return (
    <div className="stat-grid compact khata-stats project-khata-stats">
      <Stat label="Sales" amount={data.totalSale} hint={salesHint || null} />
      <Stat label="Received" amount={data.received} hint={receivedHint} tone="stat-in" />
      <Stat label="Remaining" amount={data.remaining} tone="stat-now" />
      <Stat label="Bill" amount={data.totalBill} hint={expenseHint} />
      <Stat label="Paid out" amount={data.totalPaid} />
      <Stat label="Udhar on project" amount={data.payable} tone="stat-out" />
      <Stat label="Cash" amount={data.cash} hint={countHint(data.cashCount, formatTimes(data.cashCount))} />
      <Stat label="Cheque" amount={data.cheque} hint={countHint(data.chequeCount, formatTimes(data.chequeCount))} />
      <Stat label="Online" amount={data.online} hint={countHint(data.onlineCount, formatTimes(data.onlineCount))} />
      <Stat label="Pay order" amount={data.payOrder} hint={countHint(data.payOrderCount, formatTimes(data.payOrderCount))} />
      <Stat label="Bank" amount={data.bank} hint={countHint(data.bankCount, formatTimes(data.bankCount))} />
      <Stat
        label="Udhar booked"
        amount={data.credit}
        hint={countHint(data.creditCount, formatTimes(data.creditCount))}
      />
      <Stat label="Cash in" amount={data.cashIn} tone="stat-in" />
      <Stat label="Cash out" amount={data.cashOut} tone="stat-out" />
      <Stat
        label="Cash net"
        amount={data.cashNet}
        tone={data.cashNet >= 0 ? "stat-in" : "stat-out"}
      />
    </div>
  );
}
